/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

'use client';

import { LinkData, NodeData, PhaseData } from '../../types/details-panel';
import { useCopy } from '../../hook/useCopy';
import Icons from '../../icons/icons';
import Separator from '../system/separator';
import ExpandableBadge from './expandable-badge';
import { ExpandableTextBox } from './expandable-text';
import TextWithCopy from './text-with-copy';
import TimeRange from './time-range';
import { formatDuration } from '../../lib/utils';


export interface SelectedElement {
  type: 'node' | 'link';
  data: NodeData | LinkData;
}

interface DetailPanelProps {
  selectedElement: SelectedElement;
  onClose: () => void;
}

// ── helpers ────────────────────────────────────────────────────────────────

function normalizeToString(value: string | string[] | undefined): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(' ');
  return '';
}

// ── components ─────────────────────────────────────────────────────────────

function Code({ data }: any) {
  return (
    <div className='max-w-[467px] overflow-hidden'>
      <pre className="mt-1 bg-[#0000007A] py-[11px] px-3 rounded text-xs text-body-subtle whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

/**
 * Renders a single phase (decision / execution / receipt) with its title,
 * timing sub-header, error status, and input/output payloads.
 *
 * Accepts the phase object directly so it can be reused for any phase
 */
function PhaseSection({
  title,
  phase,
}: {
  title: string;
  phase: PhaseData | null | undefined;
}) {
  if (!phase) return null;

  const statusCode =
    phase.status?.value?.code ?? null;
  const hasError = statusCode?.toUpperCase() === 'ERROR';

  return (
    <div className="space-y-4">
      <h2 className="text-body-lg font-semibold text-body-secondary">
        {title}
      </h2>

      {/* Timing sub-header */}
      <TimeAndDuration
        start_time={phase.startTime?.start_time}
        end_time={phase.endTime?.end_time}
        duration={phase.durationMs}
      />

      {/* Error status badge */}
      {hasError && (
        <ExpandableBadge
          text={statusCode}
          description={phase.status?.value?.message ?? ''}
        />
      )}

      {/* Status Object — only render if status exists */}
      {phase.status?.value && (
        <Code data={phase.status.value} />
      )}

      <div className="space-y-4">
        {phase.input && (
          <ExpandableTextBox
            label={phase.input.field ?? ''}
            content={normalizeToString(phase.input.value ?? undefined)}
          />
        )}
        {phase.output && (
          <ExpandableTextBox
            label={phase.output.field ?? ''}
            content={normalizeToString(phase.output.value ?? undefined)}
          />
        )}
      </div>

      <Separator className="bg-[#40565A] opacity-40 mt-1" />
    </div>
  );
}

/**
 * Renders all three phases for DELEGATES_TO / USES_TOOL / HANDOFF edges.
 */
function PhaseDisplay({ data }: { data: LinkData }) {
  const { decision, execution, receipt } = data.tray ?? {};

  return (
    <div className="space-y-6">
      <PhaseSection title="Decision Phase" phase={decision} />
      <PhaseSection title="Execution Phase" phase={execution} />
      <PhaseSection title="Receipt Phase" phase={receipt} />
    </div>
  );
}

function TimeAndDuration({
  start_time,
  end_time,
  duration,
}: {
  start_time: string | undefined;
  end_time: string | undefined;
  duration: string | number | undefined;
}) {
  const getDurationInMs = (dur: string | number | undefined): number | undefined => {
    if (dur === undefined || dur === null || dur === '') return undefined;
    if (typeof dur === 'number') return dur;
    const num = parseFloat(dur);
    if (isNaN(num)) return undefined;
    const lower = dur.toLowerCase();
    if (lower.includes('s') && !lower.includes('ms')) {
      return num * 1000;
    }
    return num;
  };

  const ms = getDurationInMs(duration);
  const formatted = ms !== undefined ? formatDuration(ms) : duration;

  return (
    <div className="flex items-center gap-x-1">
      <TimeRange startTime={start_time} endTime={end_time} />
      <p className="flex gap-1 rounded-full bg-[#FFFFFF14] pb-[3px] pl-[5px] pr-2 pt-0.5 text-body-sm text-body-subtle">
        <span>{Icons.ClockCircleLinear()}</span>
        <span>{formatted}</span>
      </p>
    </div>
  );
}

export default function DetailPanel({
  selectedElement,
  onClose,
}: DetailPanelProps) {
  const { copiedKey, copy, isCopied } = useCopy({ duration: 2000 });

  const { type, data } = selectedElement;

  const toolNode = type === 'node' && (data as NodeData).category === 'tool';

  const agentNode = type === 'node' && (data as NodeData).category === 'agent';

  const SCOPE_KEYS = [
    'scope.agentic.invocation',
    'scope.agentic.session',
    'scope.agentic.turn',
  ] as const;

  const turnNode =
    type === 'node' && (data as LinkData).tray?.edgeType === 'INVOKES';
  const turnFactId = turnNode && (data as NodeData).factId;
  const turnTraceId = turnNode && (data as NodeData).traceId;

  // Edges
  const invokesEdge =
    type === 'link' && (data as LinkData).edgeType === 'INVOKES';
  const delegatesEdge =
    type === 'link' && (data as LinkData).edgeType === 'DELEGATES_TO';
  const usesToolEdge =
    type === 'link' && (data as LinkData).edgeType === 'USES_TOOL';
  const handOffEdge =
    type === 'link' && (data as LinkData).edgeType === 'HANDOFF';
  const isEdge = invokesEdge || delegatesEdge || usesToolEdge || handOffEdge;
  const phaseEdge = delegatesEdge || usesToolEdge || handOffEdge;

  return (
    <div
      className="relative flex h-full w-72 shrink-0 flex-col space-y-6 overflow-y-auto border-l border-line-secondary bg-canvas-background-secondary p-4"
      style={{ minWidth: '500px' }}
    >
      <div className="flex items-center">
        {turnNode && (
          <TimeAndDuration
            start_time={(data as LinkData).tray?.startTime?.start_time}
            end_time={(data as LinkData).tray?.endTime?.end_time}
            duration={(data as LinkData).tray?.durationMs}
          />
        )}

        {isEdge && <p>{(data as LinkData)?.edgeType}</p>}

        <button
          onClick={onClose}
          aria-label="Close detail panel"
          className="ml-auto size-4 text-body-subtle"
        >
          {Icons.Close()}
        </button>
      </div>

      {/* Edge type + status */}
      {(isEdge || turnNode) && (
        <div className="space-y-4">
          <Code
            data={
              isEdge
                ? (data as LinkData).statusObj
                : (data as LinkData).tray?.status
            }
          />
        </div>
      )}

      {/* Top-level input / output payload */}
      {(isEdge || turnNode) && (data as LinkData).tray?.input && (
        <div className="space-y-4">
          <ExpandableTextBox
            label={(data as LinkData).tray!.input!.field ?? ''}
            content={normalizeToString((data as LinkData).tray!.input!.value ?? undefined)}
          />
          <ExpandableTextBox
            label={(data as LinkData).tray!.output?.field ?? ''}
            content={normalizeToString((data as LinkData).tray!.output?.value ?? undefined)}
          />
        </div>
      )}

      {/* Three-phase breakdown */}
      {phaseEdge && <PhaseDisplay data={data as LinkData} />}

      {/* Tool description */}
      {toolNode && (data as NodeData).description?.value !== null && (
        <ExpandableTextBox
          label={(data as NodeData)?.description?.field ?? ''}
          content={normalizeToString(
            (data as NodeData)?.description?.value ?? ''
          )}
        />
      )}

      {/* Agent system prompt */}
      {agentNode && (data as NodeData).systemPrompt?.value != null && (
        <ExpandableTextBox
          label={(data as NodeData)?.systemPrompt?.field ?? ''}
          content={normalizeToString(
            (data as NodeData)?.systemPrompt?.value ?? ''
          )}
        />
      )}

      {/* Agent scope fields */}
      {agentNode &&
        SCOPE_KEYS.some((key) => (data as NodeData).tray?.[key] != null) && (
          <div className="flex flex-wrap gap-2">
            {SCOPE_KEYS.map((key) => {
              const label = key.split('.').pop();
              const value = (data as NodeData).tray?.[key];
              if (value == null) return null;
              return (
                <TextWithCopy
                  key={key}
                  text={value}
                  isCopied={copiedKey === key}
                  label={label}
                  truncate
                  handleCopy={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await copy(value, key);
                  }}
                />
              );
            })}
          </div>
        )}

      {/* Tool scope fields */}
      {toolNode &&
        SCOPE_KEYS.some((key) => (data as NodeData).tray?.[key] != null) && (
          <div className="flex flex-wrap gap-2">
            {SCOPE_KEYS.map((key) => {
              const label = key.split('.').pop();
              const value = (data as NodeData).tray?.[key];
              if (value == null) return null;
              return (
                <TextWithCopy
                  key={key}
                  text={value}
                  isCopied={isCopied(key)}
                  label={label}
                  truncate
                  handleCopy={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await copy(value, key);
                  }}
                />
              );
            })}
          </div>
        )}

      {/* Edge fact id and turn id */}
      {isEdge &&
        ((data as LinkData).tray?.factIds?.length ||
          (data as LinkData).tray?.turnIds?.length) ? (
        <div className="flex flex-wrap gap-2">
          {(data as LinkData).tray?.factIds?.map(
            (factId: string, index: number) => (
              <TextWithCopy
                key={`fact-${index}`}
                text={factId}
                isCopied={copiedKey === `fact-${index}`}
                label="Fact ID"
                truncate
                handleCopy={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await copy(factId, `fact-${index}`);
                }}
              />
            )
          )}
          {(data as LinkData).tray?.turnIds?.map(
            (turnId: string, index: number) => (
              <TextWithCopy
                key={`turn-${index}`}
                text={turnId}
                isCopied={copiedKey === `turn-${index}`}
                label="Turn ID"
                truncate
                handleCopy={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await copy(turnId, `turn-${index}`);
                }}
              />
            )
          )}
        </div>
      ) : null}

      {/* Turn fact id and trace id */}
      {(turnFactId || turnTraceId) && (
        <div className="flex flex-wrap gap-2">
          {turnFactId && (
            <TextWithCopy
              text={turnFactId}
              isCopied={copiedKey === `turn-${turnFactId}`}
              label="Fact ID"
              truncate
              handleCopy={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await copy(turnFactId, `turn-${turnFactId}`);
              }}
            />
          )}
          {turnTraceId && (
            <TextWithCopy
              text={turnTraceId}
              isCopied={copiedKey === `turn-${turnTraceId}`}
              label="Trace ID"
              truncate
              handleCopy={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await copy(turnTraceId, `turn-${turnTraceId}`);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
